"use client";
import { useEffect, useRef, useState } from "react";
import { api, useApp } from "./provider";

/** One active player across the page, with bounded loading and renewed grants on retry. */
export function ManagedVideo({ src, videoId, poster, active = true, controls = true, className, label }: {
  src?: string; videoId?: string; poster?: string; active?: boolean;
  controls?: boolean; className?: string; label: string;
}) {
  const { t, error } = useApp();
  const ref = useRef<HTMLVideoElement>(null);
  const requestId = useRef(0);
  const playOnLoad = useRef(false);
  const [url, setUrl] = useState(src || "");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    requestId.current++;
    setUrl(src || ""); setFailed(false); setBusy(false);
    return () => { requestId.current++; };
  }, [src, videoId]);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const pauseOther = (event: Event) => {
      if ((event as CustomEvent).detail !== video) video.pause();
    };
    const hide = () => { if (document.hidden) video.pause(); };
    document.addEventListener("kys-video-play", pauseOther);
    document.addEventListener("visibilitychange", hide);
    const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(entries => {
      if (!entries[0].isIntersecting || entries[0].intersectionRatio < 0.25) video.pause();
    }, { threshold: [0, 0.25] });
    observer?.observe(video);
    return () => {
      video.pause(); observer?.disconnect();
      document.removeEventListener("kys-video-play", pauseOther);
      document.removeEventListener("visibilitychange", hide);
    };
  }, [url]);
  useEffect(() => { if (!active) ref.current?.pause(); }, [active]);
  useEffect(() => {
    if (url && playOnLoad.current && active) {
      playOnLoad.current = false;
      void ref.current?.play().catch(() => setBusy(false));
    }
  }, [url, active]);
  useEffect(() => {
    if (!busy) return;
    const timer = setTimeout(() => { setBusy(false); setFailed(true); ref.current?.pause(); }, 15000);
    return () => clearTimeout(timer);
  }, [busy]);
  async function start() {
    const generation = ++requestId.current;
    setFailed(false); setBusy(true);
    try {
      const next = videoId ? (await api<{ url: string }>("play", { id: videoId })).url : src;
      if (generation !== requestId.current) return;
      if (!next) throw new Error("unavailable");
      playOnLoad.current = true;
      if (next === url) {
        ref.current?.load();
        playOnLoad.current = false;
        void ref.current?.play().catch(() => setBusy(false));
      }
      setUrl(next);
      // Browser controls start playback after a protected media grant is ready.
      setBusy(false);
    } catch (e) {
      if (generation !== requestId.current) return;
      setBusy(false); setFailed(true); error(e);
    }
  }
  return <div style={{ position: "relative" }}>
    <video ref={ref} className={className} controls={controls && active && !!url} playsInline
      preload="none" poster={poster} src={url || undefined} aria-label={label}
      onPlay={(event) => {
        if (!active) { event.currentTarget.pause(); return; }
        document.dispatchEvent(new CustomEvent("kys-video-play", { detail: event.currentTarget }));
      }}
      onWaiting={() => setBusy(true)} onPlaying={() => { setBusy(false); setFailed(false); }}
      onCanPlay={() => setBusy(false)} onPause={() => setBusy(false)}
      onError={() => { setBusy(false); setFailed(true); }} />
    {active && (!url || failed) && <button type="button" className="button" disabled={busy} onClick={() => void start()}
      style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}>
      {busy ? t("Loading…", "加载中…") : failed ? t("Retry video", "重试视频") : t("Play video", "播放视频")}
    </button>}
    {busy && url && <span role="status" style={{ position: "absolute", left: 12, bottom: 50, background: "white", padding: 6, borderRadius: 8 }}>
      {t("Loading video…", "正在加载视频…")}
    </span>}
  </div>;
}
